import { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Alert,
  Card,
  Checkbox,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
const emptyEvent = {
  name: '',
  type: 'school_vacation',
  scope: 'country',
  country: 'IL',
  city: '',
  startDate: '',
  endDate: '',
  impact: 0.5,
  categories: [],
  enabled: true,
};
const emptyCity = {
  city: '',
  country: 'IL',
  type: 'general',
  seasonalSensitivity: 1,
  notes: '',
  enabled: true,
};
const clone = (value) => JSON.parse(JSON.stringify(value));
const days = [
  ['Sunday', 'א׳'],
  ['Monday', 'ב׳'],
  ['Tuesday', 'ג׳'],
  ['Wednesday', 'ד׳'],
  ['Thursday', 'ה׳'],
  ['Friday', 'ו׳'],
  ['Saturday', 'שבת'],
];
const categoryLabels = {
  park: 'פארק',
  beach: 'חוף',
  supermarket: 'סופרמרקט',
  pharmacy: 'בית מרקחת',
  bank: 'בנק',
  post_office: 'דואר',
  electronics_store: 'חנות אלקטרוניקה',
  shopping_mall: 'קניון',
  restaurant: 'מסעדה',
  cafe: 'בית קפה',
  gym: 'חדר כושר',
  gas_station: 'תחנת דלק',
  default: 'ברירת מחדל',
};
const AiAlgorithmSettingsPage = ({ request, user }) => {
  const [draft, setDraft] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [versions, setVersions] = useState([]);
  const [placeId, setPlaceId] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const isAdmin = user?.role === 'admin';
  const load = async () => {
    setBusy(true);
    setMessage('');
    try {
      const [payload, history] = await Promise.all([
        request('/bo/ai-algorithm-settings'),
        request('/bo/ai-algorithm-settings/versions'),
      ]);
      setDraft(payload.settings);
      setDefaults(payload.defaults);
      setVersions(history || []);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (key, value) =>
    setDraft((current) => ({
      ...current,
      [key]: value,
    }));
  const setCalendar = (key, value) =>
    setDraft((current) => ({
      ...current,
      calendar: {
        ...current.calendar,
        [key]: value,
      },
    }));
  const setContainerContext = (key, value) =>
    setDraft((current) => ({
      ...current,
      containerContext: {
        ...current.containerContext,
        [key]: value,
      },
    }));
  const save = async () => {
    setBusy(true);
    setMessage('');
    try {
      const saved = await request('/bo/ai-algorithm-settings', {
        method: 'PUT',
        body: JSON.stringify(draft),
      });
      setDraft(saved);
      await load();
      setMessage('הגדרות AI נשמרו');
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };
  const test = async () => {
    if (!placeId.trim()) return;
    setBusy(true);
    setMessage('');
    setTestResult(null);
    try {
      setTestResult(
        await request('/bo/ai-algorithm-settings/test', {
          method: 'POST',
          body: JSON.stringify({
            placeId: placeId.trim(),
            settings: draft,
          }),
        }),
      );
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };
  const rollback = async (version) => {
    if (!window.confirm(`לחזור לגרסה ${version}? הפעולה תיצור גרסה חדשה.`)) return;
    setBusy(true);
    try {
      await request(`/bo/ai-algorithm-settings/versions/${version}/rollback`, {
        method: 'POST',
      });
      await load();
      setMessage(`שוחזר תוכן גרסה ${version}`);
    } catch (error) {
      setMessage(error.message);
    } finally {
      setBusy(false);
    }
  };
  const updateEvent = (index, patch) =>
    setCalendar(
      'events',
      draft.calendar.events.map((item, i) =>
        i === index
          ? {
            ...item,
            ...patch,
          }
          : item,
      ),
    );
  const updateCity = (index, patch) =>
    setCalendar(
      'cityProfiles',
      draft.calendar.cityProfiles.map((item, i) =>
        i === index
          ? {
            ...item,
            ...patch,
          }
          : item,
      ),
    );
  const setBaseline = (key, value) =>
    setDraft((current) => ({
      ...current,
      baseline: {
        ...current.baseline,
        [key]: value,
      },
    }));
  const updateCategory = (index, patch) =>
    setBaseline(
      'categories',
      draft.baseline.categories.map((item, i) =>
        i === index
          ? {
            ...item,
            ...patch,
          }
          : item,
      ),
    );
  const updateTimeRule = (categoryIndex, ruleIndex, patch) =>
    updateCategory(categoryIndex, {
      timeRules: draft.baseline.categories[categoryIndex].timeRules.map((rule, i) =>
        i === ruleIndex
          ? {
            ...rule,
            ...patch,
          }
          : rule,
      ),
    });
  const updateDay = (categoryIndex, day, value) =>
    updateCategory(categoryIndex, {
      dayAdjustments: {
        ...draft.baseline.categories[categoryIndex].dayAdjustments,
        [day]: Number(value),
      },
    });
  const updatePopularity = (index, patch) =>
    setBaseline(
      'popularityRules',
      draft.baseline.popularityRules.map((item, i) =>
        i === index
          ? {
            ...item,
            ...patch,
          }
          : item,
      ),
    );
  if (!draft)
    return (
      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '22px',
          direction: 'rtl',
          maxWidth: '1680px',
          margin: '0 auto',
          [theme.breakpoints.down('sm')]: {
            maxWidth: '100%',
            gap: '14px',
          },
        })}
      >
        <Typography
          sx={{
            fontSize: 20,
            fontWeight: 'bold',
          }}
        >
          אלגוריתם AI
        </Typography>
        <Typography>{message || 'טוען…'}</Typography>
      </Stack>
    );
  return (
    <Stack
      dir="rtl"
      sx={(theme) => ({
        display: 'grid',
        gap: '20px',
        direction: 'rtl',
        maxWidth: '1680px',
        margin: '0 auto',
        [theme.breakpoints.down('sm')]: {
          maxWidth: '100%',
          gap: '14px',
        },
        paddingBottom: '40px',
      })}
    >
      <Stack
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          marginBottom: '4px',
          direction: 'rtl',
          [theme.breakpoints.down('lg')]: {
            display: 'grid',
          },
          flexDirection: 'row',
          [theme.breakpoints.down('sm')]: {
            minWidth: '0',
            gap: '12px',
            position: 'relative',
            paddingLeft: '0',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            width: '100%',
          },
        })}
      >
        <Stack>
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 'bold',
            }}
          >
            אלגוריתם AI
          </Typography>
          <Typography>
            גרסת פרומפט {draft.version || 1}
            {draft.updatedBy?.name ? ` · עודכן על ידי ${draft.updatedBy.name}` : ''}
          </Typography>
        </Stack>
        <Button disabled={busy || !isAdmin} onClick={save}>
          שמירת הגדרות
        </Button>
      </Stack>
      {message && (
        <Alert
          sx={{
            padding: '12px 16px',
            borderRadius: '12px',
            background: '#ecfdf3',
            color: '#067647',
            fontWeight: '700',
          }}
        >
          {message}
        </Alert>
      )}
      {!isAdmin && (
        <Alert
          sx={{
            padding: '12px 16px',
            borderRadius: '12px',
            background: '#fffaeb',
            color: '#b54708',
            fontWeight: '700',
            borderRightColor: '#f79009',
          }}
        >
          ניתן לצפייה בלבד. שינוי ובדיקה דורשים הרשאת Admin.
        </Alert>
      )}

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Paper
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '18px',
            '& p': {
              margin: '6px 0 0',
            },
            [theme.breakpoints.down(701)]: {
              alignItems: 'stretch',
              flexDirection: 'column',
            },
          })}
        >
          <Stack>
            <Typography>מודל ופרומפט</Typography>
            <Typography
              sx={(theme) => ({
                color: 'var(--muted)',
                [theme.breakpoints.down('lg')]: {
                  margin: '4px 0 0',
                  fontSize: '12px',
                  display: 'none',
                },
                margin: '2px 0 0',
                fontSize: '11px',
              })}
            >
              מבנה תשובת JSON נשאר מוגן בשרת ואינו ניתן לעריכה.
            </Typography>
          </Stack>
          <Stack
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              minHeight: '42px',
              '& )': {
                minWidth: '180px',
                flex: '1',
              },
              padding: '9px 10px',
              border: '1px solid #e7ebf0',
              borderRadius: '9px',
              background: '#fbfcfd',
              '& small': {
                color: 'var(--muted)',
                direction: 'ltr',
                textAlign: 'right',
              },
            }}
          >
            <Checkbox
              checked={draft.enabled}
              disabled={!isAdmin}
              onChange={(e) => set('enabled', e.target.checked)}
            />
            הערכת AI פעילה
          </Stack>
        </Paper>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(170px, 1fr))',
            gap: '12px',
            [theme.breakpoints.down('lg')]: {
              gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
            },
            [theme.breakpoints.down(701)]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <Stack>
            מודל
            <TextField
              value={draft.model}
              disabled={!isAdmin}
              onChange={(e) => set('model', e.target.value)}
              fullWidth
            />
          </Stack>
          <Stack>
            סטייה מרבית מה־baseline
            <TextField
              type="number"
              min="0"
              max="4"
              step="0.1"
              value={draft.maxBaselineDeviation}
              disabled={!isAdmin}
              onChange={(e) => set('maxBaselineDeviation', Number(e.target.value))}
              fullWidth
            />
          </Stack>
          <Stack>
            משקל תשובת AI
            <TextField
              type="number"
              min="0"
              max="1"
              step="0.05"
              value={draft.aiWeight}
              disabled={!isAdmin}
              onChange={(e) => set('aiWeight', Number(e.target.value))}
              fullWidth
            />
          </Stack>
          <Stack>
            ציון מרבי ללא מידע חי
            <TextField
              type="number"
              min="1"
              max="10"
              value={draft.maximumScoreWithoutLiveEvidence}
              disabled={!isAdmin}
              onChange={(e) => set('maximumScoreWithoutLiveEvidence', Number(e.target.value))}
              fullWidth
            />
          </Stack>
          <Stack>
            Confidence מינימלי
            <TextField
              type="number"
              min="0"
              max="100"
              value={draft.confidenceMin}
              disabled={!isAdmin}
              onChange={(e) => set('confidenceMin', Number(e.target.value))}
              fullWidth
            />
          </Stack>
          <Stack>
            Confidence מקסימלי
            <TextField
              type="number"
              min="0"
              max="100"
              value={draft.confidenceMax}
              disabled={!isAdmin}
              onChange={(e) => set('confidenceMax', Number(e.target.value))}
              fullWidth
            />
          </Stack>
          <Stack>
            ימי היסטוריה
            <TextField
              type="number"
              min="1"
              max="365"
              value={draft.historyLookbackDays}
              disabled={!isAdmin}
              onChange={(e) => set('historyLookbackDays', Number(e.target.value))}
              fullWidth
            />
          </Stack>
          <Stack>
            חלון שעות דומות (±)
            <TextField
              type="number"
              min="0"
              max="12"
              value={draft.similarHourWindow}
              disabled={!isAdmin}
              onChange={(e) => set('similarHourWindow', Number(e.target.value))}
              fullWidth
            />
          </Stack>
          <Stack
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '8px',
              minHeight: '42px',
              '& )': {
                minWidth: '180px',
                flex: '1',
              },
              padding: '9px 10px',
              border: '1px solid #e7ebf0',
              borderRadius: '9px',
              background: '#fbfcfd',
              '& small': {
                color: 'var(--muted)',
                direction: 'ltr',
                textAlign: 'right',
              },
            }}
          >
            <Checkbox
              checked={draft.containerContext?.enabled !== false}
              disabled={!isAdmin}
              onChange={(e) => setContainerContext('enabled', e.target.checked)}
            />
            שקלול עומס מקום מכיל
          </Stack>
          <Stack>
            השפעה מרבית של המתחם
            <TextField
              type="number"
              min="0"
              max="4"
              step="0.1"
              value={draft.containerContext?.maximumAdjustment ?? 2}
              disabled={!isAdmin}
              onChange={(e) => setContainerContext('maximumAdjustment', Number(e.target.value))}
              fullWidth
            />
          </Stack>
        </Stack>
        <Stack>
          System Prompt
          <TextField
            rows="16"
            value={draft.systemPrompt}
            disabled={!isAdmin}
            onChange={(e) => set('systemPrompt', e.target.value)}
            multiline
            fullWidth
            sx={{
              direction: 'ltr',
              textAlign: 'left',
              fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
              lineHeight: '1.5',
            }}
          />
        </Stack>
        <Stack>
          הנחיות עסקיות נוספות
          <TextField
            rows="5"
            value={draft.additionalInstructions || ''}
            disabled={!isAdmin}
            placeholder="הנחיות שמתווספות לפרומפט הבסיסי"
            onChange={(e) => set('additionalInstructions', e.target.value)}
            multiline
            fullWidth
          />
        </Stack>
        <Stack
          sx={{
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end',
          }}
        >
          <Button
            type="button"
            disabled={!isAdmin || !defaults}
            onClick={() =>
              setDraft({
                ...clone(defaults),
                version: draft.version,
                updatedAt: draft.updatedAt,
                updatedBy: draft.updatedBy,
              })
            }
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            שחזור ברירת מחדל לטיוטה
          </Button>
        </Stack>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Stack>
          <Typography>חישוב Baseline</Typography>
          <Typography
            sx={(theme) => ({
              color: 'var(--muted)',
              [theme.breakpoints.down('lg')]: {
                margin: '4px 0 0',
                fontSize: '12px',
                display: 'none',
              },
              margin: '2px 0 0',
              fontSize: '11px',
            })}
          >
            ערך הבסיס נקבע לפי קטגוריה, שעה, יום וכמות דירוגים. החוק הראשון שמתאים לשעה או
            לפופולריות הוא זה שיופעל.
          </Typography>
        </Stack>
        <Stack
          sx={{
            display: 'grid',
            gap: '10px',
          }}
        >
          {draft.baseline.categories.map((category, categoryIndex) => (
            <Paper
              key={category.key}
              sx={(theme) => ({
                border: '1px solid var(--line)',
                borderRadius: '14px',
                background: '#f8fafc',
                overflow: 'hidden',
                '& > summary': {
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '14px 16px',
                  cursor: 'pointer',
                },
                [theme.breakpoints.down(701)]: {
                  '& > summary': {
                    alignItems: 'flex-start',
                    flexDirection: 'column',
                  },
                },
              })}
            >
              <Button>
                <Typography>{categoryLabels[category.key] || category.key}</Typography>
                <Typography>
                  בסיס {category.baselineLoad} · טווח {category.minLoad}–{category.maxLoad}
                </Typography>
              </Button>
              <Stack
                sx={{
                  display: 'grid',
                  gap: '18px',
                  padding: '0 16px 16px',
                  borderTop: '1px solid var(--line)',
                  '& > *:first-child': {
                    marginTop: '16px',
                  },
                }}
              >
                <Typography
                  sx={(theme) => ({
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(140px, 1fr))',
                    gap: '10px',
                    [theme.breakpoints.down(701)]: {
                      gridTemplateColumns: '1fr',
                    },
                  })}
                >
                  <Stack>
                    ערך בסיס
                    <TextField
                      type="number"
                      min="1"
                      max="10"
                      step="0.5"
                      value={category.baselineLoad}
                      disabled={!isAdmin}
                      onChange={(e) =>
                        updateCategory(categoryIndex, {
                          baselineLoad: Number(e.target.value),
                        })
                      }
                      fullWidth
                    />
                  </Stack>
                  <Stack>
                    מינימום
                    <TextField
                      type="number"
                      min="1"
                      max="10"
                      step="0.5"
                      value={category.minLoad}
                      disabled={!isAdmin}
                      onChange={(e) =>
                        updateCategory(categoryIndex, {
                          minLoad: Number(e.target.value),
                        })
                      }
                      fullWidth
                    />
                  </Stack>
                  <Stack>
                    מקסימום
                    <TextField
                      type="number"
                      min="1"
                      max="10"
                      step="0.5"
                      value={category.maxLoad}
                      disabled={!isAdmin}
                      onChange={(e) =>
                        updateCategory(categoryIndex, {
                          maxLoad: Number(e.target.value),
                        })
                      }
                      fullWidth
                    />
                  </Stack>
                </Typography>
                <Stack>
                  <Typography>התאמות לפי יום</Typography>
                  <Stack
                    sx={(theme) => ({
                      display: 'grid',
                      gridTemplateColumns: 'repeat(7, minmax(80px, 1fr))',
                      gap: '8px',
                      [theme.breakpoints.down('md')]: {
                        gridTemplateColumns: 'repeat(4, minmax(80px, 1fr))',
                      },
                      [theme.breakpoints.down(701)]: {
                        gridTemplateColumns: '1fr',
                      },
                    })}
                  >
                    {days.map(([day, label]) => (
                      <Stack key={day}>
                        {label}
                        <TextField
                          type="number"
                          min="-5"
                          max="5"
                          step="0.1"
                          value={category.dayAdjustments?.[day] || 0}
                          disabled={!isAdmin}
                          onChange={(e) => updateDay(categoryIndex, day, e.target.value)}
                          fullWidth
                        />
                      </Stack>
                    ))}
                  </Stack>
                </Stack>
                <Stack>
                  <Typography>חוקי שעות</Typography>
                  <Stack
                    sx={{
                      display: 'grid',
                      gap: '8px',
                      marginBottom: '10px',
                    }}
                  >
                    {category.timeRules.map((rule, ruleIndex) => (
                      <Stack
                        key={`${category.key}-${ruleIndex}`}
                        sx={(theme) => ({
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3, minmax(120px, 1fr)) 75px',
                          gap: '8px',
                          alignItems: 'end',
                          [theme.breakpoints.down(701)]: {
                            gridTemplateColumns: '1fr',
                          },
                        })}
                      >
                        <Stack>
                          משעה
                          <TextField
                            type="number"
                            min="0"
                            max="23"
                            value={rule.startHour}
                            disabled={!isAdmin}
                            onChange={(e) =>
                              updateTimeRule(categoryIndex, ruleIndex, {
                                startHour: Number(e.target.value),
                              })
                            }
                            fullWidth
                          />
                        </Stack>
                        <Stack>
                          עד שעה
                          <TextField
                            type="number"
                            min="0"
                            max="24"
                            value={rule.endHour}
                            disabled={!isAdmin}
                            onChange={(e) =>
                              updateTimeRule(categoryIndex, ruleIndex, {
                                endHour: Number(e.target.value),
                              })
                            }
                            fullWidth
                          />
                        </Stack>
                        <Stack>
                          התאמה
                          <TextField
                            type="number"
                            min="-5"
                            max="5"
                            step="0.1"
                            value={rule.adjustment}
                            disabled={!isAdmin}
                            onChange={(e) =>
                              updateTimeRule(categoryIndex, ruleIndex, {
                                adjustment: Number(e.target.value),
                              })
                            }
                            fullWidth
                          />
                        </Stack>
                        <Button
                          disabled={!isAdmin}
                          onClick={() =>
                            updateCategory(categoryIndex, {
                              timeRules: category.timeRules.filter((_, i) => i !== ruleIndex),
                            })
                          }
                          sx={{
                            color: '#b42318',
                            borderColor: '#f1b9b4',
                            background: '#fff5f4',
                          }}
                        >
                          הסרה
                        </Button>
                      </Stack>
                    ))}
                  </Stack>
                  <Button
                    disabled={!isAdmin}
                    onClick={() =>
                      updateCategory(categoryIndex, {
                        timeRules: [
                          ...category.timeRules,
                          {
                            startHour: 9,
                            endHour: 17,
                            adjustment: 0,
                          },
                        ],
                      })
                    }
                    sx={{
                      background: '#edf2f7',
                      color: 'var(--text)',
                      '&:hover': {
                        background: '#ddebff',
                        color: '#1269e8',
                      },
                    }}
                  >
                    הוספת חוק שעות
                  </Button>
                </Stack>
              </Stack>
            </Paper>
          ))}
        </Stack>
        <Stack>
          <Typography>מדרגות פופולריות</Typography>
          <Typography
            sx={(theme) => ({
              color: 'var(--muted)',
              [theme.breakpoints.down('lg')]: {
                margin: '4px 0 0',
                fontSize: '12px',
                display: 'none',
              },
              margin: '2px 0 0',
              fontSize: '11px',
            })}
          >
            ההתאמה נקבעת לפי מספר הדירוגים של המקום.
          </Typography>
          <Stack
            sx={{
              display: 'grid',
              gap: '8px',
              marginBottom: '10px',
            }}
          >
            {draft.baseline.popularityRules.map((rule, index) => (
              <Stack
                key={`popularity-${index}`}
                sx={(theme) => ({
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, minmax(120px, 1fr)) 75px',
                  gap: '8px',
                  alignItems: 'end',
                  [theme.breakpoints.down(701)]: {
                    gridTemplateColumns: '1fr',
                  },
                })}
              >
                <Stack>
                  מכמות
                  <TextField
                    type="number"
                    min="0"
                    value={rule.minCount}
                    disabled={!isAdmin}
                    onChange={(e) =>
                      updatePopularity(index, {
                        minCount: Number(e.target.value),
                      })
                    }
                    fullWidth
                  />
                </Stack>
                <Stack>
                  עד כמות
                  <TextField
                    type="number"
                    min="0"
                    placeholder="ללא תקרה"
                    value={rule.maxCount ?? ''}
                    disabled={!isAdmin}
                    onChange={(e) =>
                      updatePopularity(index, {
                        maxCount: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                    fullWidth
                  />
                </Stack>
                <Stack>
                  התאמה
                  <TextField
                    type="number"
                    min="-5"
                    max="5"
                    step="0.1"
                    value={rule.adjustment}
                    disabled={!isAdmin}
                    onChange={(e) =>
                      updatePopularity(index, {
                        adjustment: Number(e.target.value),
                      })
                    }
                    fullWidth
                  />
                </Stack>
                <Button
                  disabled={!isAdmin}
                  onClick={() =>
                    setBaseline(
                      'popularityRules',
                      draft.baseline.popularityRules.filter((_, i) => i !== index),
                    )
                  }
                  sx={{
                    color: '#b42318',
                    borderColor: '#f1b9b4',
                    background: '#fff5f4',
                  }}
                >
                  הסרה
                </Button>
              </Stack>
            ))}
          </Stack>
          <Button
            disabled={!isAdmin}
            onClick={() =>
              setBaseline('popularityRules', [
                ...draft.baseline.popularityRules,
                {
                  minCount: 0,
                  maxCount: null,
                  adjustment: 0,
                },
              ])
            }
            sx={{
              background: '#edf2f7',
              color: 'var(--text)',
              '&:hover': {
                background: '#ddebff',
                color: '#1269e8',
              },
            }}
          >
            הוספת מדרגה
          </Button>
        </Stack>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Typography>בדיקת פרומפט לפי Place ID</Typography>
        <Typography
          sx={(theme) => ({
            color: 'var(--muted)',
            [theme.breakpoints.down('lg')]: {
              margin: '4px 0 0',
              fontSize: '12px',
              display: 'none',
            },
            margin: '2px 0 0',
            fontSize: '11px',
          })}
        >
          מריץ קריאת AI אמיתית אך אינו יוצר דיווח ואינו משנה עומס.
        </Typography>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gridTemplateColumns: 'minmax(260px, 1fr) auto',
            gap: '12px',
            alignItems: 'end',
            [theme.breakpoints.down(701)]: {
              gridTemplateColumns: '1fr',
            },
          })}
        >
          <Stack>
            Place ID
            <TextField
              value={placeId}
              disabled={!isAdmin}
              onChange={(e) => setPlaceId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && test()}
              fullWidth
            />
          </Stack>
          <Button disabled={busy || !isAdmin || !placeId.trim()} onClick={test}>
            בדיקת פרומפט
          </Button>
        </Stack>
        {testResult && (
          <Stack
            sx={{
              display: 'grid',
              gap: '12px',
              padding: '16px',
              border: '1px solid #b2ddff',
              borderRadius: '14px',
              background: '#f5fbff',
              '& summary': {
                cursor: 'pointer',
                fontWeight: '800',
              },
              '& pre': {
                maxHeight: '520px',
                overflow: 'auto',
                padding: '14px',
                borderRadius: '10px',
                background: '#101828',
                color: '#e4e7ec',
                direction: 'ltr',
                textAlign: 'left',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
              },
            }}
          >
            <Stack
              sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: 'repeat(4, minmax(120px, 1fr))',
                gap: '10px',
                '& article': {
                  display: 'grid',
                  gap: '4px',
                  padding: '12px',
                  borderRadius: '10px',
                  background: '#fff',
                },
                '& strong': {
                  fontSize: '24px',
                },
                [theme.breakpoints.down('lg')]: {
                  gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
                },
                [theme.breakpoints.down(701)]: {
                  gridTemplateColumns: '1fr',
                },
              })}
            >
              <Card>
                <Typography>Baseline</Typography>
                <Typography>{testResult.estimate?.baselineScore ?? '—'}</Typography>
              </Card>
              <Card>
                <Typography>תוצאת AI</Typography>
                <Typography>{testResult.estimate?.rawModelResult?.loadScore ?? '—'}</Typography>
              </Card>
              <Card>
                <Typography>ציון סופי</Typography>
                <Typography>{testResult.estimate?.loadScore ?? '—'}</Typography>
              </Card>
              <Card>
                <Typography>Confidence</Typography>
                <Typography>{testResult.estimate?.confidence ?? '—'}</Typography>
              </Card>
              <Card>
                <Typography>מקור חגים</Typography>
                <Typography
                  sx={{
                    fontSize: '17px',
                  }}
                >
                  {testResult.debug?.preparedPlaces?.[0]?.calendarContext?.automaticSource
                    ?.provider || '—'}
                </Typography>
                <Typography>
                  {testResult.debug?.preparedPlaces?.[0]?.calendarContext?.automaticSource?.status}
                </Typography>
              </Card>
            </Stack>
            <Typography>
              <Typography>סיבה:</Typography> {testResult.estimate?.reason} ·{' '}
              <Typography>מגמה:</Typography> {testResult.estimate?.trend}
            </Typography>
            <Paper open>
              <Button>הקלט שנשלח ותוצאת המודל</Button>
              <Typography>{JSON.stringify(testResult, null, 2)}</Typography>
            </Paper>
          </Stack>
        )}
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Paper
          sx={(theme) => ({
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: '18px',
            '& p': {
              margin: '6px 0 0',
            },
            [theme.breakpoints.down(701)]: {
              alignItems: 'stretch',
              flexDirection: 'column',
            },
          })}
        >
          <Stack>
            <Typography>חגים, חופשות ואירועים</Typography>
            <Typography
              sx={(theme) => ({
                color: 'var(--muted)',
                [theme.breakpoints.down('lg')]: {
                  margin: '4px 0 0',
                  fontSize: '12px',
                  display: 'none',
                },
                margin: '2px 0 0',
                fontSize: '11px',
              })}
            >
              חגים אוטומטיים מ־
              <Link href="https://www.hebcal.com/" target="_blank" rel="noreferrer">
                Hebcal
              </Link>{' '}
              בישראל ומ־
              <Link href="https://date.nager.at/" target="_blank" rel="noreferrer">
                Nager.Date
              </Link>{' '}
              במדינות נתמכות, לצד מידע מקומי מנוהל.
            </Typography>
          </Stack>
          <Stack
            sx={{
              display: 'flex',
              gap: '14px',
              flexWrap: 'wrap',
            }}
          >
            <Stack
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                minHeight: '42px',
                '& )': {
                  minWidth: '180px',
                  flex: '1',
                },
                padding: '9px 10px',
                border: '1px solid #e7ebf0',
                borderRadius: '9px',
                background: '#fbfcfd',
                '& small': {
                  color: 'var(--muted)',
                  direction: 'ltr',
                  textAlign: 'right',
                },
              }}
            >
              <Checkbox
                checked={draft.calendar.enabled}
                disabled={!isAdmin}
                onChange={(e) => setCalendar('enabled', e.target.checked)}
              />
              פעיל
            </Stack>
            <Stack
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                minHeight: '42px',
                '& )': {
                  minWidth: '180px',
                  flex: '1',
                },
                padding: '9px 10px',
                border: '1px solid #e7ebf0',
                borderRadius: '9px',
                background: '#fbfcfd',
                '& small': {
                  color: 'var(--muted)',
                  direction: 'ltr',
                  textAlign: 'right',
                },
              }}
            >
              <Checkbox
                checked={draft.calendar.automaticPublicHolidays}
                disabled={!isAdmin}
                onChange={(e) => setCalendar('automaticPublicHolidays', e.target.checked)}
              />
              חגים אוטומטיים
            </Stack>
          </Stack>
        </Paper>
        <Stack
          sx={{
            maxWidth: '260px',
          }}
        >
          השפעה מרבית על ההקשר
          <TextField
            type="number"
            min="0"
            max="3"
            step="0.1"
            value={draft.calendar.maximumAdjustment}
            disabled={!isAdmin}
            onChange={(e) => setCalendar('maximumAdjustment', Number(e.target.value))}
            fullWidth
          />
        </Stack>
        <Stack
          sx={{
            display: 'grid',
            gap: '10px',
          }}
        >
          {draft.calendar.events.map((item, index) => (
            <Card
              key={`event-${index}`}
              sx={{
                display: 'grid',
                gridTemplateColumns:
                  '1.2fr 150px 100px 110px 110px 140px 140px 80px minmax(180px, 1fr) 75px',
                gap: '8px',
                alignItems: 'end',
                overflowX: 'auto',
              }}
            >
              <TextField
                placeholder="שם"
                value={item.name}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    name: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                value={item.type}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    type: e.target.value,
                  })
                }
                select
                fullWidth
              >
                <MenuItem value="school_vacation">חופשת לימודים</MenuItem>
                <MenuItem value="holiday">חג</MenuItem>
                <MenuItem value="holiday_eve">ערב חג</MenuItem>
                <MenuItem value="local_event">אירוע מקומי</MenuItem>
                <MenuItem value="tourism_season">עונת תיירות</MenuItem>
              </TextField>
              <TextField
                value={item.scope}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    scope: e.target.value,
                  })
                }
                select
                fullWidth
              >
                <MenuItem value="country">מדינה</MenuItem>
                <MenuItem value="city">עיר</MenuItem>
                <MenuItem value="global">כללי</MenuItem>
              </TextField>
              <TextField
                placeholder="מדינה / IL"
                value={item.country || ''}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    country: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                placeholder="עיר"
                value={item.city || ''}
                disabled={!isAdmin || item.scope !== 'city'}
                onChange={(e) =>
                  updateEvent(index, {
                    city: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                type="date"
                value={item.startDate}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    startDate: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                type="date"
                value={item.endDate}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    endDate: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                type="number"
                step="0.1"
                min="-3"
                max="3"
                title="השפעה"
                value={item.impact}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    impact: Number(e.target.value),
                  })
                }
                fullWidth
              />
              <TextField
                placeholder="קטגוריות, מופרדות בפסיק"
                value={(item.categories || []).join(', ')}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateEvent(index, {
                    categories: e.target.value
                      .split(',')
                      .map((x) => x.trim())
                      .filter(Boolean),
                  })
                }
                fullWidth
              />
              <Button
                disabled={!isAdmin}
                onClick={() =>
                  setCalendar(
                    'events',
                    draft.calendar.events.filter((_, i) => i !== index),
                  )
                }
                sx={{
                  color: '#b42318',
                  borderColor: '#f1b9b4',
                  background: '#fff5f4',
                }}
              >
                הסרה
              </Button>
            </Card>
          ))}
        </Stack>
        <Button
          disabled={!isAdmin}
          onClick={() => setCalendar('events', [...draft.calendar.events, clone(emptyEvent)])}
          sx={{
            background: '#edf2f7',
            color: 'var(--text)',
            '&:hover': {
              background: '#ddebff',
              color: '#1269e8',
            },
          }}
        >
          הוספת אירוע
        </Button>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Typography>פרופילי ערים</Typography>
        <Typography
          sx={(theme) => ({
            color: 'var(--muted)',
            [theme.breakpoints.down('lg')]: {
              margin: '4px 0 0',
              fontSize: '12px',
              display: 'none',
            },
            margin: '2px 0 0',
            fontSize: '11px',
          })}
        >
          רגישות עונתית מכפילה את השפעת האירועים המתאימים לעיר.
        </Typography>
        <Stack
          sx={{
            display: 'grid',
            gap: '10px',
          }}
        >
          {draft.calendar.cityProfiles.map((item, index) => (
            <Card
              key={`city-${index}`}
              sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: '1fr 120px 130px 100px 1.5fr 75px',
                gap: '8px',
                alignItems: 'end',
                [theme.breakpoints.down('lg')]: {
                  gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
                },
                [theme.breakpoints.down(701)]: {
                  gridTemplateColumns: '1fr',
                },
              })}
            >
              <TextField
                placeholder="עיר"
                value={item.city}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateCity(index, {
                    city: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                placeholder="מדינה / IL"
                value={item.country || ''}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateCity(index, {
                    country: e.target.value,
                  })
                }
                fullWidth
              />
              <TextField
                value={item.type}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateCity(index, {
                    type: e.target.value,
                  })
                }
                select
                fullWidth
              >
                <MenuItem value="general">כללי</MenuItem>
                <MenuItem value="tourism">תיירות</MenuItem>
                <MenuItem value="residential">מגורים</MenuItem>
                <MenuItem value="business">עסקים</MenuItem>
              </TextField>
              <TextField
                type="number"
                min="0"
                max="3"
                step="0.1"
                title="רגישות עונתית"
                value={item.seasonalSensitivity}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateCity(index, {
                    seasonalSensitivity: Number(e.target.value),
                  })
                }
                fullWidth
              />
              <TextField
                placeholder="הערות"
                value={item.notes || ''}
                disabled={!isAdmin}
                onChange={(e) =>
                  updateCity(index, {
                    notes: e.target.value,
                  })
                }
                fullWidth
              />
              <Button
                disabled={!isAdmin}
                onClick={() =>
                  setCalendar(
                    'cityProfiles',
                    draft.calendar.cityProfiles.filter((_, i) => i !== index),
                  )
                }
                sx={{
                  color: '#b42318',
                  borderColor: '#f1b9b4',
                  background: '#fff5f4',
                }}
              >
                הסרה
              </Button>
            </Card>
          ))}
        </Stack>
        <Button
          disabled={!isAdmin}
          onClick={() =>
            setCalendar('cityProfiles', [...draft.calendar.cityProfiles, clone(emptyCity)])
          }
          sx={{
            background: '#edf2f7',
            color: 'var(--text)',
            '&:hover': {
              background: '#ddebff',
              color: '#1269e8',
            },
          }}
        >
          הוספת עיר
        </Button>
      </Stack>

      <Stack
        sx={(theme) => ({
          display: 'grid',
          gap: '18px',
          padding: '20px',
          border: '1px solid var(--line)',
          borderRadius: '16px',
          background: '#fff',
          boxShadow: 'var(--shadow-sm)',
          [theme.breakpoints.down('sm')]: {
            padding: '14px',
          },
        })}
      >
        <Typography>היסטוריית גרסאות</Typography>
        <Stack
          sx={(theme) => ({
            display: 'grid',
            gap: '10px',
            '& article': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px',
              padding: '12px 14px',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              background: '#f8fafc',
            },
            '& article div': {
              display: 'grid',
              gap: '3px',
            },
            '& small': {
              color: 'var(--muted)',
            },
            [theme.breakpoints.down(701)]: {
              '& article': {
                alignItems: 'stretch',
                flexDirection: 'column',
              },
            },
          })}
        >
          {versions.map((item) => (
            <Card key={item.version}>
              <Stack>
                <Typography>גרסה {item.version}</Typography>
                <Typography>
                  {new Date(item.createdAt).toLocaleString('he-IL')} ·{' '}
                  {item.updatedBy?.name || item.updatedBy?.email || 'מערכת'} ·{' '}
                  {item.action === 'rollback' ? 'שחזור' : 'עדכון'}
                </Typography>
              </Stack>
              <Button
                disabled={!isAdmin || busy}
                onClick={() => rollback(item.version)}
                sx={{
                  background: '#edf2f7',
                  color: 'var(--text)',
                  '&:hover': {
                    background: '#ddebff',
                    color: '#1269e8',
                  },
                }}
              >
                שחזור
              </Button>
            </Card>
          ))}
        </Stack>
      </Stack>
    </Stack>
  );
};
export default AiAlgorithmSettingsPage;
